import { formatTaskCardDue, TaskCardProps } from 'shared/components/TaskCard';
import {
  getHubSSVAUploadBackyardRoute,
  getHubSSVAUploadFrontyardRoute,
  getHubSSVAUploadInteriorRoute,
  getHubSSVAUploadSellerDisclosureDocumentRoute,
  getHubSSVAUploadWholeHomeRoute,
} from 'shared/helpers/routes';
import { getPreClosingOffer, useOfferContext } from 'sellDirect/OfferContext';
import { OdProtosWebData_RecordedFeature as RecordedFeature } from '__generated__/globalTypes';
import { VirtualAssessment_sellDirect_getLead_lead_latestSelfServeVirtualAssessment_ssvaRecordings as SSVARecording } from 'sellDirect/graphql/__generated__/VirtualAssessment';

interface UseSSVASubtasksResult {
  loading: boolean;
  tasks?: TaskCardProps[];
  /** progress of all SSVA subtasks, range from 0 to 100. */
  progress?: number;
}

/** Tasks for SSVA */
export const useSSVASubtasks = (): UseSSVASubtasksResult => {
  const { shouldSeeAgentExperience, offer, loading } = useOfferContext();

  if (loading) {
    return {
      loading: true,
    };
  }

  const preClosingOffer = getPreClosingOffer(offer);

  if (!preClosingOffer) {
    return { loading: false };
  }

  const {
    ssvaData: {
      ssvaRecordings,
      ssvaQuestionnaireSubmitted,
      ssvaInteriorRecordingsSubmitted,
      ssvaBackyardRecordingsSubmitted,
      ssvaFrontyardRecordingsSubmitted,
      ssvaWholeHomeRecordingsSubmitted,
      ssvaSellerDisclosureDocumentRecordingsSubmitted,
      ssvaRecordingsSubmitted,
    },
    ssvaFormLink,
  } = preClosingOffer.ssvaData;

  const uploadTaskIsDone = (feature: RecordedFeature) => {
    switch (feature) {
      case RecordedFeature.INTERIOR:
        return ssvaInteriorRecordingsSubmitted;
      case RecordedFeature.FRONTYARD:
        return ssvaFrontyardRecordingsSubmitted;
      case RecordedFeature.BACKYARD:
        return ssvaBackyardRecordingsSubmitted;
      case RecordedFeature.WHOLE_HOME:
        return ssvaWholeHomeRecordingsSubmitted;
      default:
        return false;
    }
  };

  const uploadTaskProgress = (feature: RecordedFeature, ssvaRecordings: SSVARecording[]) => {
    if (uploadTaskIsDone(feature)) {
      return 100;
    }
    return ssvaRecordings.some((recording) => recording.recordedFeature == feature)
      ? 50
      : undefined;
  };

  const due = preClosingOffer.expiresAt
    ? formatTaskCardDue(preClosingOffer.expiresAt.toLocalDate())
    : undefined;

  const interiorTask: TaskCardProps = {
    title: 'Interior video',
    subtitle: '(8 mins)',
    analyticsName: 'ssva-interior-video-task',
    url: getHubSSVAUploadInteriorRoute(),
    due,
    progress: uploadTaskProgress(RecordedFeature.INTERIOR, ssvaRecordings),
    isDone: ssvaInteriorRecordingsSubmitted,
  };

  const frontyardTask: TaskCardProps = {
    title: 'Video of the front yard',
    subtitle: '(1 min)',
    analyticsName: 'ssva-frontyard-video-task',
    url: getHubSSVAUploadFrontyardRoute(),
    due,
    progress: uploadTaskProgress(RecordedFeature.FRONTYARD, ssvaRecordings),
    isDone: ssvaFrontyardRecordingsSubmitted,
  };

  const backyardTask: TaskCardProps = {
    title: 'Video of the backyard',
    subtitle: '(1 min)',
    analyticsName: 'ssva-backyard-video-task',
    url: getHubSSVAUploadBackyardRoute(),
    due,
    progress: uploadTaskProgress(RecordedFeature.BACKYARD, ssvaRecordings),
    isDone: ssvaBackyardRecordingsSubmitted,
  };

  const questionnaireTask: TaskCardProps = {
    title: 'Answer a few questions',
    subtitle: '(1 min)',
    analyticsName: 'ssva-questionnaire-task',
    url: ssvaFormLink,
    due,
    isDone: ssvaQuestionnaireSubmitted,
    // disabled if the recordings have not been submitted or if this task has already been completed
    disabled: !ssvaRecordingsSubmitted || ssvaQuestionnaireSubmitted,
  };

  const wholeHomeTask: TaskCardProps = {
    title: 'Share a video of the home',
    subtitle: '(1 min)',
    analyticsName: 'ssva-whole-home-video-task',
    url: getHubSSVAUploadWholeHomeRoute(),
    due,
    progress: uploadTaskProgress(RecordedFeature.WHOLE_HOME, ssvaRecordings),
    isDone: ssvaWholeHomeRecordingsSubmitted,
  };

  const sellerDisclosureDocumentTask: TaskCardProps = {
    title: 'Upload seller’s disclosure',
    subtitle: '(1 min)',
    analyticsName: 'ssva-seller-disclosure-document-task',
    url: getHubSSVAUploadSellerDisclosureDocumentRoute(),
    due,
    progress: uploadTaskProgress(RecordedFeature.SELLER_DISCLOSURE_DOCUMENT, ssvaRecordings),
    isDone: ssvaSellerDisclosureDocumentRecordingsSubmitted,
  };

  const directSellerTasks = [interiorTask, frontyardTask, backyardTask, questionnaireTask];
  const agentTasks = [
    preClosingOffer.lead.isListed ? sellerDisclosureDocumentTask : wholeHomeTask,
    questionnaireTask,
  ];
  const tasks = shouldSeeAgentExperience ? agentTasks : directSellerTasks;
  /** progress is the sum of the progress for all sub tasks divided by the number of tasks. */
  const progress =
    tasks.map((task) => task.progress ?? 0).reduce((prev, cur) => prev + cur) / tasks.length ||
    undefined;
  return {
    loading,
    tasks,
    progress,
  };
};
